// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Rapport mode on ruxd's compiled preview (contract gap 4, reusex-ufcm.7):
 * `GET /reports/ressourcekortlaegning/preview` answers the report as PNG
 * pages plus each section's readiness. This module maps that answer onto the
 * strip (`pageModels`), the "Før indberetning" verdict (`buildChecklist`) and
 * the centre tile's page navigation. A server from before the preview (404)
 * keeps the client-composed pages in `pages.ts` instead.
 *
 * Pure: no React, no DOM.
 */

import type { ReportCover, ReportPdfVersion, ReportSectionReadiness, SurveyFractions } from '../api/types';
import { canSend } from '../indberetning/model';
import { fractionsReason, type Checklist } from './checklist';
import { INCOMPLETE_TEXT, pageModels, READY_TEXT, type PageId, type PageModel } from './pages';

/** The section a strip page stands for, or null when the server did not send it. */
export function sectionFor(sections: readonly ReportSectionReadiness[] | null, id: PageId): ReportSectionReadiness | null {
  return sections?.find((s) => s.id === id) ?? null;
}

/** "Sagsdata ufuldstændig" for a section that is not ready yet names nothing. */
function sectionReasons(s: ReportSectionReadiness): string[] {
  if (s.ready) return [];
  return s.missing.length > 0 ? [...s.missing] : [`${s.title ?? s.id} ${INCOMPLETE_TEXT.toLowerCase()}`];
}

/**
 * The strip's cards with the server's verdict on each document page (the
 * section of the same id). A page the server sent no section for keeps the
 * client's verdict; Indberetning reads the (server-aware) checklist and the
 * PDF card is unchanged.
 */
export function serverPageModels(
  list: Checklist,
  versions: readonly ReportPdfVersion[] | null,
  sections: readonly ReportSectionReadiness[] | null,
): PageModel[] {
  const pages = pageModels(list, versions);
  if (!sections) return pages;
  return pages.map((p) => {
    const s = sectionFor(sections, p.id);
    if (!s) return p;
    const reasons = sectionReasons(s);
    return { ...p, ready: s.ready, reasons, figure: s.ready ? READY_TEXT : 'Mangler data' };
  });
}

/**
 * The checklist's verdict when the server judges the sections: ready when
 * every titled section is, "Alle typer screenet" is ok (it needs the resource
 * keys and stays the GUI's) and the fractions can be sent. The rows stay the
 * client's, for their figures; `missing` carries the server's wording, each
 * entry a phrase of its own (see `indberetHint`).
 */
export function withServerSections(
  list: Checklist,
  sections: readonly ReportSectionReadiness[] | null,
  fractions: SurveyFractions | null,
): Checklist {
  if (!sections || sections.length === 0) return list;
  const missing: string[] = [];
  for (const s of sections) {
    if (s.title === null) continue;
    for (const r of sectionReasons(s)) if (!missing.includes(r)) missing.push(r);
  }
  const screened = list.rows.find((r) => r.id === 'screened');
  const screenedOk = !screened || screened.state === 'ok';
  if (screened && !screenedOk && screened.missing) missing.push(`Mangler ${screened.missing}`);
  const sendable = fractions !== null && canSend(fractions);
  if (!sendable && missing.length === 0) missing.push(`Mangler ${fractionsReason(fractions)}`);
  const ready = sendable && screenedOk && sections.every((s) => s.title === null || s.ready);
  return { ...list, ready, missing, source: 'server' };
}

/** 0-based inclusive page range of a titled section; template-only sections after the last one fold into it. */
export function sectionRange(
  sections: readonly ReportSectionReadiness[],
  id: string,
  pageCount: number,
): { start: number; end: number } | null {
  const at = sections.findIndex((s) => s.id === id);
  const s = sections[at];
  if (!s || s.first_page === null || pageCount <= 0) return null;
  const start = Math.min(Math.max(s.first_page - 1, 0), pageCount - 1);
  const next = sections.slice(at + 1).find((x) => x.title !== null && x.first_page !== null);
  const end = next?.first_page != null ? Math.max(start, Math.min(next.first_page - 2, pageCount - 1)) : pageCount - 1;
  return { start, end };
}

/** The titled section a (0-based) page belongs to, or null when the server could not say. */
export function sectionOfPage(sections: readonly ReportSectionReadiness[], index: number, pageCount: number): string | null {
  for (const s of sections) {
    if (s.title === null) continue;
    const r = sectionRange(sections, s.id, pageCount);
    if (r && index >= r.start && index <= r.end) return s.id;
  }
  return null;
}

/** The page ppi asked of the server: crisp on a dense screen, inside the contract's 30..200. */
export function previewPpi(devicePixelRatio: number): number {
  const dpr = Number.isFinite(devicePixelRatio) && devicePixelRatio > 0 ? devicePixelRatio : 1;
  return Math.round(Math.min(200, Math.max(96, 96 * Math.min(dpr, 2))));
}

/** What the Forside's image is: "Modelbillede" or the chosen photo / 360° / upload. */
export function coverCaption(cover: ReportCover | null): string {
  if (!cover || cover.source === null) return 'Modelbillede (punktsky set skråt oppefra)';
  if (cover.source === 'upload') return 'Uploadet billede';
  const crop = cover.region ? ' · udsnit' : '';
  return cover.source === 'frame' ? `Foto #${cover.id}${crop}` : `360° #${cover.id}${crop}`;
}

/** Normalise a dragged rectangle (two corners, 0..1) into a region; null when it is too small to mean one. */
export function dragRegion(
  a: { x: number; y: number },
  b: { x: number; y: number },
  min = 0.03,
): [number, number, number, number] | null {
  const clamp = (v: number) => Math.min(1, Math.max(0, v));
  const x0 = clamp(Math.min(a.x, b.x));
  const y0 = clamp(Math.min(a.y, b.y));
  const x1 = clamp(Math.max(a.x, b.x));
  const y1 = clamp(Math.max(a.y, b.y));
  if (x1 - x0 < min || y1 - y0 < min) return null;
  const r6 = (v: number) => Math.round(v * 1e6) / 1e6;
  return [r6(x0), r6(y0), r6(x1 - x0), r6(y1 - y0)];
}
